import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    { label: category ? category.name : '分类' },
  ];

  const collectionSchema = category
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${category.name} - 91制片厂`,
        description: category.description,
        url: `https://www.91pianzhi.com/category/${category.slug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '91制片厂',
          url: 'https://www.91pianzhi.com',
        },
      }
    : null;

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-screen">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 mb-3">分类未找到</h1>
            <p className="text-foreground-400 mb-6">抱歉，您访问的分类不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24 min-h-screen">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {collectionSchema && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
            />
          )}

          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <aside className="lg:w-60 shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="font-heading text-base font-semibold text-foreground-50 mb-4">内容分类</h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                        cat.slug === slug
                          ? 'bg-primary-500/20 text-primary-400 font-medium'
                          : 'text-foreground-400 hover:text-foreground-50 hover:bg-background-200/30'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      <span>{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-500">{cat.count}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <i className={`${category.icon} text-2xl text-primary-400`}></i>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50">
                    {category.name}
                  </h1>
                </div>
                <p className="text-foreground-400 text-sm">{category.description}</p>
              </div>

              {articles.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} size="small" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                    <i className="ri-folder-open-line text-2xl text-foreground-500"></i>
                  </div>
                  <p className="text-foreground-400">该分类暂无内容</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="py-12"></div>
      </main>
      <Footer />
    </>
  );
}